import SmoothScroll from './utils/SmoothScroll'
import Three from './three/core/Three'
import RecordScene from './three/scenes/RecordScene'
import RecordSlider from './slider/RecordSlider'
import PageTransitions from './navigation/PageTransitions'
import { leaveCaption, arriveCaption, settled } from './utils/captionLines'
import { bindPointerLook } from './utils/pointerLook'
import Loader from './utils/Loader'
import { loadRecordModel } from './three/utils/RecordModel'
import gsap from 'gsap'

/**
 * Composition root. Records come from WordPress via wp_localize_script (window.psdigital.records).
 */
export default class App {
	#leftEarly = false // record link: the out-animation already ran inside #leaveTo

	constructor() {
		this.loader = new Loader() // before anything starts loading
		this.smoothScroll = new SmoothScroll()
		this.scene = new RecordScene(window.psdigital?.records ?? [])
		this.three = new Three(this.scene)
		this.slider = null
		this.transitions = new PageTransitions({
			onPage: page => this.#showPage(page),
			beforeLeave: url => this.#leaveTo(url),
			animateOut: () => this.#animateOut(),
			animateIn: () => this.#animateIn(),
			onBusy: busy => this.smoothScroll.lock(busy), // no scrolling mid-transition
		})

		this.#showPage(PageTransitions.readPage(), { immediate: true })
		this.three.run()
		this.#firstLoad()
	}

	// Loading screen until the models and textures are in, then the records rise in and the
	// page content follows, the same way it does after a page transition.
	async #firstLoad() {
		const { caption, rest } = App.#pageParts()
		gsap.set(rest, { autoAlpha: 0 })
		this.smoothScroll.lock(true)
		const loaded = this.loader.ready(loadRecordModel(), document.fonts?.ready)
		// the text comes in as the records are still rising, once the loading screen has cleared
		const shown = loaded.then(() => new Promise(resolve => gsap.delayedCall(this.loader.outDuration, resolve)))
		this.slider ? this.slider.arriveCaption(shown) : arriveCaption(caption, shown)
		await loaded
		this.loader.hide()
		const intro = this.scene.intro()
		await shown
		gsap.to(rest, { autoAlpha: 1, duration: 0.5 })
		await intro
		this.smoothScroll.lock(false)
	}

	// Scroll up first; then for a record link, centre it in the slider or flip to it before the swap.
	async #leaveTo(url) {
		this.#leftEarly = false
		await this.smoothScroll.scrollToTop()
		const slug = this.scene.slugFor(url)
		if (!slug) return
		await this.#animateOut() // caption and content go first...
		this.#leftEarly = true // ...so swup's own out-step has nothing left to do
		await (this.slider ? this.slider.settleOn(url) : this.scene.swap(slug)) // ...then the record moves
	}

	// Page transition: the caption's lines mask out and the next page's mask in - the
	// home and record captions sit in the same spot, so they read as one caption
	// changing. Everything else in the page simply fades.
	#animateOut() {
		if (this.#leftEarly) return Promise.resolve()
		const { caption, rest } = App.#pageParts()
		return Promise.all([
			this.slider ? this.slider.leaveCaption() : leaveCaption(caption),
			settled(vars => gsap.to(rest, { autoAlpha: 0, duration: 0.4, ...vars })),
		])
	}

	#animateIn() {
		const { caption, rest } = App.#pageParts()
		// the caption text holds until the record has finished isolating / returning to the
		// slider; not awaited, so a quick next click isn't blocked behind the wait
		const ready = this.scene.whenSettled()
		this.slider ? this.slider.arriveCaption(ready) : arriveCaption(caption, ready)
		return settled(vars => gsap.fromTo(rest, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5, ...vars }))
	}

	/** The caption, and everything in #swup outside it. */
	static #pageParts() {
		const container = document.querySelector('#swup')
		const caption = container.querySelector('.record-caption')
		const rest = [...container.children].filter(child => !child.contains(caption))
		return { caption, rest }
	}

	#showPage({ record, slider, anchor }, { immediate = false } = {}) {
		this.slider?.destroy()
		this.scene.release() // any hover/press from the page we left
		this.slider = slider
			? new RecordSlider(slider, {
					onHover: (slug, on, look) => this.scene.hover(slug, on, look),
					onPress: (slug, on) => this.scene.press(slug, on),
				})
			: null
		this.slider?.jumpTo(this.scene.activeSlug) // come back to the record we left from

		this.scene.slider = this.slider
		this.scene.anchor = anchor
		// the isolated record follows the pointer over its anchor, like a sleeve in the slider
		if (record && anchor) bindPointerLook(anchor, (on, look) => this.scene.hover(record, on, look))
		record ? this.scene.isolate(record, immediate) : this.scene.restore(immediate)

		this.smoothScroll.lenis.scrollTo(0, { immediate: true, force: true }) // force: scrolling is locked mid-visit
	}
}
